import React, { MouseEvent, ReactNode } from 'react';
import Breadcrumb from '../../Breadcrumb';
import CustomTabv2, { CustomTabItem } from '../../custom/CustomTabv2';
import QueryEditorComponent from './QueryEditorComponent';
import QueryAccessComponent from './QueryAccessComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from 'config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { changeQuery, changeQueryDirty, changeQueryIsUpdate, clearQuery } from '../../../redux/actions/QueryActions';
import ComponentName from '../../../constant/ComponentName';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import QueryVariableService from '../../../service/QueryVariableService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import { QueryVariable } from '../../../domain/enrichment/EnrichmentDomain';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import CustomLoading from '../../custom/CustomLoading';
import { clearPage } from '../../../redux/actions/PageActions';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../../ComponentErrorLog';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;

class QueryEditorContainer extends StandardEditorContainerComponent<Props, State, QueryVariable> {
  public state = new State();

  private queryVariableSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private initialDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private syncDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private isInitialDirtyResetScheduled: boolean = false;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly queryVariableService: QueryVariableService = ServiceContainer.getInstance().get(ServiceNames.QUERY_VARIABLE);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.ENRICHMENT,
      icon: 'enrichment',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      query: { queryVariable, isDirty },
      isUpdate,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={queryVariable.name}
          description={queryVariable.description}
          status={queryVariable?.stage || 'DRAFT'}
          isUpdate={isUpdate}
          icon={'enrichment'}
          component={ComponentName.QUERY_VARIABLE}
          onChangeName={this.onChangeName}
          onChangeDescription={this.onChangeDescription}
          onSave={this.saveVariable}
          onDeploy={this.deployVariable}
          onUndeploy={this.undeployVariable}
          onRemove={this.removeVariable}
          disableSave={!RoleHelper.canEdit('EnrichmentQuery')}
          disableRemove={!RoleHelper.canEdit('EnrichmentQuery')}
          disableNameChange={!RoleHelper.canEdit('EnrichmentQuery')}
          disableDescriptionChange={!RoleHelper.canEdit('EnrichmentQuery')}
          disableDeploy={!RoleHelper.canDeploy('EnrichmentQuery') || isDirty}
          disableUndeploy={!RoleHelper.canDeploy('EnrichmentQuery')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearQuery } = this.props;
    if (this.initialDirtyResetTimer) {
      clearTimeout(this.initialDirtyResetTimer);
    }
    if (this.syncDirtyResetTimer) {
      clearTimeout(this.syncDirtyResetTimer);
    }
    clearQuery();
    super.componentWillUnmount();
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      query: { isUpdate },
      changeQueryDirty,
    } = this.props;
    if (!prevProps.query.isUpdate && isUpdate && !this.isInitialDirtyResetScheduled) {
      this.isInitialDirtyResetScheduled = true;
      this.initialDirtyResetTimer = setTimeout(() => {
        changeQueryDirty(false);
      }, 1200);
    }
  }

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private getVariableIdFromRoute = () => {
    const { changeQueryIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeQueryIsUpdate(id != undefined);
    return id;
  };

  protected _startTracker = async () => {
    const { changeQueryIsUpdate } = this.props;
    const variableId = this.getVariableIdFromRoute();
    if (variableId == undefined) {
      changeQueryIsUpdate(false);
    }
    const databaseVariableQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('id', variableId));
    this.queryVariableSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, databaseVariableQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.queryVariableSubscription]);
  };

  protected tracker = async () => {
    const { changeQueryIsUpdate, changeQuery, changeQueryDirty } = this.props;
    if (this.queryVariableSubscription?.ready()) {
      const variableId = this.getVariableIdFromRoute();
      if (variableId == undefined) {
        changeQueryIsUpdate(false);
        changeQueryDirty(false);
        this.setState({ loading: false });
        RoleHelper.setCurrentAccess('Edit');
        return;
      }
      const databaseVariableQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('id', variableId)).build();
      const databaseVariable: QueryVariable | undefined = await this.repository.find(databaseVariableQuery);
      if (databaseVariable == undefined) return;
      changeQuery(databaseVariable);
      changeQueryDirty(false);
      if (this.syncDirtyResetTimer) {
        clearTimeout(this.syncDirtyResetTimer);
      }
      this.syncDirtyResetTimer = setTimeout(() => {
        changeQueryDirty(false);
      }, 1200);
      RoleHelper.setCurrentAccess(databaseVariable);
      this.setState({ loading: false });
    }
  };

  protected _stopTracker = async () => {
    await this.queryVariableSubscription?.stop();
    this.computation?.stop();
  };

  private onChangeName = (name: string) => {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    if (isStringValid(name)) {
      queryVariable.name = name.trim();
      changeQuery(queryVariable);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    queryVariable.description = description;
    changeQuery(queryVariable);
  };

  private switchToUpdate = () => {
    const {
      router: { navigate, params },
      query: { queryVariable },
      enterComponent,
    } = this.props;
    if (queryVariable.name == '') return;
    enterComponent({ component: ComponentName.ENRICHMENT, id: queryVariable.name });
    navigate(`/enrichment${params.folderId ? `/folder/${params.folderId}` : ''}/query/${queryVariable.name}`, { replace: true });
    setTimeout(() => {
      this.resubscribeTracker();
    });
  };

  private saveVariable = async () => {
    const {
      query: { queryVariable },
      isUpdate,
      lastCategory,
      changeQueryIsUpdate,
    } = this.props;
    try {
      const isSaved = await this.queryVariableService.save(queryVariable, isUpdate, lastCategory);
      if (isSaved) {
        changeQueryIsUpdate(true);
        if (!isUpdate) this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private deployVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      query: { queryVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.queryVariableService.save(queryVariable, true, {} as EnrichedComponentCategory);
        await this.queryVariableService.deploy(queryVariable);
      },
      message: LanguageUtil.get({ id: 'areyousuredeployqueryvariable' }),
    });
  };

  private undeployVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      query: { queryVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.QUERY_VARIABLE, [queryVariable.name], () => this.queryVariableService.undeploy(queryVariable.name)),
      message: LanguageUtil.get({ id: 'rule.operation.deploy.areyousureundeployed' }),
    });
  };

  private removeVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      query: { queryVariable },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.queryVariableService.delete(queryVariable.name);
        if (success) {
          navigate(`/enrichment`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.delete1', defaultMessage: 'Are you sure you want to remove?' }),
    });
  };

  protected getTabs(): CustomTabItem[] {
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        content: <QueryEditorComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <QueryAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.QUERY_VARIABLE, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.QUERY_VARIABLE} documentName={this.props.query.queryVariable.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.QUERY_VARIABLE, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: LanguageUtil.get({ id: 'common.tabs.error', defaultMessage: 'Error' }),
        content: <ComponentErrorLog component={ComponentName.QUERY_VARIABLE} documentName={this.props.query.queryVariable.name} />,
        visible: RoleHelper.canManage(ComponentName.QUERY_VARIABLE, true),
      },
    ];
  }

  protected changeItem = (item: QueryVariable) => {
    const { changeQuery } = this.props;
    changeQuery(item);
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  query: state.query,
  isUpdate: state.query.isUpdate,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = {
  changeQuery,
  changeQueryDirty,
  changeQueryIsUpdate,
  clearQuery,
  enterComponent,
  leaveComponent,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(QueryEditorContainer));
